import { getRuntimeLocale, t } from "../i18n/runtime";
import { withAppBasePath } from "../app/basePath";

const AUTH_REQUIRED_EVENT = "agent-judge:auth-required";
const AUTH_ENDPOINTS_WITH_EXPECTED_UNAUTHORIZED = new Set([
  "/api/auth/me",
  "/api/auth/login",
  "/api/auth/bootstrap",
  "/api/auth/platform",
]);

export class ApiError extends Error {
  readonly status: number;
  readonly detail: unknown;

  constructor(status: number, message: string, detail?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

function detailMessage(detail: unknown, fallback: string): string {
  if (typeof detail === "string" && detail.trim()) return detail;
  if (detail && typeof detail === "object") {
    const record = detail as Record<string, unknown>;
    if (typeof record.message === "string" && record.message.trim()) return record.message;
    if (typeof record.detail === "string" && record.detail.trim()) return record.detail;
  }
  return fallback;
}

/**
 * Backend messages remain a diagnostic fallback, not a localized UI contract.
 * Avoid leaking Chinese or English server copy into another selected locale;
 * structured error codes can replace this fallback incrementally.
 */
function localizedServerMessage(detail: unknown, fallback: string): string {
  const raw = detailMessage(detail, fallback);
  const locale = getRuntimeLocale();
  if (locale === "de-DE") return t("common.requestFailedRetry");
  if (locale === "en-US" && /[\u3400-\u9fff]/.test(raw)) return t("common.requestFailedRetry");
  return raw;
}

export async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  let response: Response;
  try {
    response = await fetch(withAppBasePath(path), {
      credentials: "same-origin",
      cache: "no-store",
      ...init,
      headers,
    });
  } catch (error) {
    throw new ApiError(0, error instanceof Error ? error.message : t("api.networkFailed"));
  }

  if (!response.ok) {
    const text = await response.text();
    let detail: unknown = text;
    try {
      detail = text ? (JSON.parse(text) as Record<string, unknown>).detail ?? JSON.parse(text) : null;
    } catch {
      // Keep the response text as the error detail.
    }
    const requestPath = path.split("?", 1)[0];
    if (response.status === 401 && !AUTH_ENDPOINTS_WITH_EXPECTED_UNAUTHORIZED.has(requestPath)) {
      window.dispatchEvent(new Event(AUTH_REQUIRED_EVENT));
    }
    throw new ApiError(
      response.status,
      localizedServerMessage(detail, `${response.status} ${response.statusText}`.trim()),
      detail,
    );
  }

  if (response.status === 204) return undefined as T;
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) return (await response.text()) as T;
  return response.json() as Promise<T>;
}

export function jsonBody(value: unknown): string {
  return JSON.stringify(value);
}

export function authRequiredEventName(): string {
  return AUTH_REQUIRED_EVENT;
}

export async function downloadFile(path: string, fallbackName: string): Promise<void> {
  let response: Response;
  try {
    response = await fetch(withAppBasePath(path), { credentials: "same-origin", cache: "no-store" });
  } catch (error) {
    throw new ApiError(0, t("api.downloadNetworkFailed"), error);
  }
  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(
      response.status,
      localizedServerMessage(text, `${response.status} ${response.statusText}`.trim()),
      text,
    );
  }
  const disposition = response.headers.get("content-disposition") ?? "";
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const quoted = disposition.match(/filename="([^"]+)"/i)?.[1];
  const filename = encoded ? decodeURIComponent(encoded) : quoted || fallbackName;
  const objectUrl = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}
